// src/lib/row-patch.js — правка CRG «по полям» (2026-10-07).
//
// Строка уходит на сервер целиком (для вставки, если сервер её не знает) плюс маркер
// `_patchFields` — какие поля изменены и ещё не доставлены. Сервер переносит в свою
// копию строки только их (backend/utils/patch-fields.cjs).
//
// Зачем: row-LWW брал строку целиком, и вкладка с устаревшими данными, тронув одно поле,
// откатывала остальные к своей старой копии (07.10, сделка 47 FR у Zack).
//
// Маркер живёт на строке и в state, и в очереди, и в LS-кэше — так недоставленная
// правка переживает перезагрузку (boot-merge App.jsx шлёт кэш целым массивом, и сервер
// по маркеру возьмёт только нужные поля). Гаснет сам: сервер пишет updatedAt входящей
// строки, эхо приходит с тем же ts, и mergeRowsServerWins (равные ts → сервер)
// заменяет строку серверной, без маркера.

const PATCH_KEY = '_patchFields';

function sameValue(a, b) {
  if (Object.is(a, b)) return true;
  try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; }
}

function isMetaKey(key) {
  return key === 'updatedAt' || key.charAt(0) === '_';
}

/**
 * Строка next с маркером изменённых полей — или null, если действие ничего не поменяло
 * (тогда ни штамповать, ни слать нечего). Поля ещё недоставленной прошлой правки (маркер
 * prev) сохраняются в маркере.
 * @param {object | undefined} prev — строка вкладки до действия
 * @param {object} next — строка после действия
 * @returns {object | null}
 */
export function patchRow(prev, next) {
  if (!prev || !next) return null;
  const changed = [];
  for (const key of new Set([...Object.keys(prev), ...Object.keys(next)])) {
    if (isMetaKey(key)) continue;
    if (!sameValue(prev[key], next[key])) changed.push(key);
  }
  if (changed.length === 0) return null;
  const pending = Array.isArray(prev[PATCH_KEY]) ? prev[PATCH_KEY] : [];
  const fields = [...pending, ...changed.filter((k) => !pending.includes(k))];
  return { ...next, [PATCH_KEY]: fields };
}

/**
 * Две версии одной строки в очереди (older раньше, newer позже) → что слать.
 * Обе — патчи: тело newer, поля — объединение (older могла не доехать: сбой, повтор).
 * Иначе newer как есть: целая строка остаётся целой; патч поверх недоставленной
 * вставки сервер, не зная id, вставит целиком (тело строки полное).
 * @param {object | undefined} older
 * @param {object} newer
 * @returns {object}
 */
export function coalescePatchRows(older, newer) {
  const a = older && Array.isArray(older[PATCH_KEY]) ? older[PATCH_KEY] : null;
  const b = newer && Array.isArray(newer[PATCH_KEY]) ? newer[PATCH_KEY] : null;
  if (!a || !b) return newer;
  return { ...newer, [PATCH_KEY]: [...a, ...b.filter((k) => !a.includes(k))] };
}

/**
 * После успешной доставки delivered: убрать из маркера row поля, доставленные с тем же
 * значением, что сейчас на строке. Поле, изменённое ещё раз после отправки, остаётся.
 * Доставлена целая строка (вставка, без маркера) — кандидаты все поля маркера row.
 * Без маркера не осталось ничего → строка без маркера (для state и кэша — «чистая»;
 * очередь такую строку должна выбросить, а не слать целиком).
 *
 * Зачем (жюри A+B 07.10): иначе маркер гаснет только эхом сервера; эхо застряло
 * (медленный WS), и следующая правка другого поля переслала бы доставленное значение
 * поверх чужого, более нового.
 * @param {object} row
 * @param {object} delivered
 * @returns {object} row как есть, если снимать нечего
 */
export function pruneDeliveredFields(row, delivered) {
  if (!row || !delivered || row.id !== delivered.id) return row;
  const pending = Array.isArray(row[PATCH_KEY]) ? row[PATCH_KEY] : null;
  if (!pending) return row;
  const sent = Array.isArray(delivered[PATCH_KEY]) ? delivered[PATCH_KEY] : pending;
  const keep = pending.filter((f) => !(sent.includes(f) && sameValue(row[f], delivered[f])));
  if (keep.length === pending.length) return row;
  if (keep.length === 0) {
    const { [PATCH_KEY]: _delivered, ...rest } = row;
    return rest;
  }
  return { ...row, [PATCH_KEY]: keep };
}
